import React from "react";
import styled from "styled-components";
import { Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";

const StyledModal = styled(Modal)`
  z-index: 1060 !important;

  .modal-dialog {
    max-width: 500px;
    width: auto;
    margin: auto;
    padding: 5px;
    display: flex;
    align-items: center;
    height: 99vh; /* fills full viewport for flex centering */
  }

  .modal-content {
    display: flex;
    flex-direction: column;
    max-width: 100%;
    max-height: 100%;
    border-radius: 7px;
    border: none;
    box-shadow: 0 12px 52px rgba(0, 0, 0, 0.1);
    overflow: hidden;
    z-index: 1060;
  }

  .modal-body {
    padding: 1rem;
    font-size: 15px;
    color: #333;
    overflow: auto;
    flex: 1 1 auto;
  }

  .modal-header {
    background-color: #ee754c;
    border: none;
    border-top-left-radius: 7px;
    border-top-right-radius: 7px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    min-height: 50px;
  }

  .modal-title {
    font-weight: bold;
    font-size: 18px;
    color: #faf6e9;
  }

  .modal-footer {
    background-color: white;
    border: none;
    padding: 0.75rem 1rem;
    display: flex;
    justify-content: flex-end;
    gap: 10px;
  }
`;

const CloseIcon = styled(FontAwesomeIcon).attrs({ icon: faXmark })`
  position: absolute;
  top: 16px;
  right: 20px;
  font-size: 26px;
  color: #faf6e9;
  cursor: pointer;
  transition: color 0.2s ease;
`;

export const CustomModal = ({
  show,
  onHide,
  title,
  body,
  footer,
  centered = true,
  topSpacing = null,
  children,
  removeCloseButton = false,
  ...rest
}) => {
  return (
    <StyledModal
      show={show}
      onHide={onHide}
      centered={centered}
      backdrop="static"
      $topSpacing={topSpacing}
      {...rest}
    >
      {(title || onHide) && (
        <div className="modal-header">
          {title && <h5 className="modal-title">{title}</h5>}
          {removeCloseButton ? (
            <></>
          ) : onHide ? (
            <CloseIcon onClick={onHide} role="button" />
          ) : (
            <></>
          )}
        </div>
      )}

      <div className="modal-body">{body || children}</div>

      {footer && <div className="modal-footer">{footer}</div>}
    </StyledModal>
  );
};

const StyledButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 10px 16px;
  border: none;
  border-radius: 8px;
  font-family:
    "Inter",
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    Roboto,
    "Helvetica Neue",
    Arial,
    sans-serif;
  font-size: ${({ $fontSize }) => $fontSize || "14px"};
  font-weight: 500;
  cursor: pointer;
  background-color: ${({ $color }) => $color || "#007bff"};
  color: white;
  transition:
    background-color 0.2s ease-in-out,
    box-shadow 0.2s ease-in-out;

  &:hover {
    background-color: ${({ $color }) => darkenColor($color || "#007bff", 0.1)};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  &:active {
    transform: scale(0.98);
  }

  &:disabled {
    background-color: #ccc !important;
    color: #666;
    cursor: not-allowed !important;
    box-shadow: none;
  }
`;

// Helper function to darken the hover color
function darkenColor(hex, amount) {
  try {
    let col = hex;
    if (col.startsWith("#")) col = col.slice(1);

    const num = parseInt(col, 16);
    let r = (num >> 16) - amount * 255;
    let g = ((num >> 8) & 0x00ff) - amount * 255;
    let b = (num & 0x0000ff) - amount * 255;

    r = Math.max(0, r);
    g = Math.max(0, g);
    b = Math.max(0, b);

    return `rgb(${r}, ${g}, ${b})`;
  } catch {
    return hex;
  }
}

export const ModernButton = ({
  children,
  color,
  fontSize,
  onClick,
  style,
  type = "button",
  disabled = false,
}) => {
  return (
    <StyledButton
      $color={color}
      onClick={onClick}
      style={style}
      $fontSize={fontSize}
      type={type}
      disabled={disabled}
    >
      {children}
    </StyledButton>
  );
};
